import React, { useEffect, useState } from 'react';
import { Modal } from '../../../components/common/Modal';
import { RoastChartLegend } from './RoastChartLegend';
import { RoastLineChart } from './RoastLineChart';

interface RoastProfileChartProps {
  chartData: {
    time: number;
    bean: number | null;
    ror: number | null;
    fire: number | null;
    airflow: number | null;
    airTemp: number | null;
    drumTemp: number | null;
    environmentTemp: number | null;
    drumRpm: number | null;
  }[];
  legendItems: { key: string; label: string; color: string }[];
  phaseAreas: { x1: number; x2: number; fill: string; opacity: number; label: string; detail?: string }[];
  xTicks: number[];
  tempAxisMax: number;
  tempTicks: number[];
  rightAxisMax: number;
  rightTicks: number[];
  hasAirTempData: boolean;
  hasDrumTempData: boolean;
  hasEnvironmentTempData: boolean;
  hasDrumRpmData: boolean;
  stageMarkers?: { key: string; time: number; temp: number; labelTop: string; labelBottom: string }[];
}

export const RoastProfileChart: React.FC<RoastProfileChartProps> = React.memo(({
  chartData,
  legendItems,
  phaseAreas,
  xTicks,
  tempAxisMax,
  tempTicks,
  rightAxisMax,
  rightTicks,
  hasAirTempData,
  hasDrumTempData,
  hasEnvironmentTempData,
  hasDrumRpmData,
  stageMarkers,
}) => {
  const [isChartModalOpen, setIsChartModalOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isContainerReady, setIsContainerReady] = useState(false);

  useEffect(() => {
    const raf = requestAnimationFrame(() => setIsContainerReady(true));
    return () => cancelAnimationFrame(raf);
  }, []);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 640);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Shared props feeding both the inline card chart and the zoom-modal chart.
  const chartProps = {
    isMobile,
    chartData,
    phaseAreas,
    xTicks,
    tempAxisMax,
    tempTicks,
    rightAxisMax,
    rightTicks,
    hasAirTempData,
    hasDrumTempData,
    hasEnvironmentTempData,
    hasDrumRpmData,
    stageMarkers,
  };

  return (
    <>
      <div
        className="h-[500px] bg-white border rounded-lg px-1 sm:px-4 pt-4 pb-2 flex flex-col cursor-zoom-in"
        role="button"
        tabIndex={0}
        onClick={() => setIsChartModalOpen(true)}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') {
            setIsChartModalOpen(true);
          }
        }}
      >
        <RoastChartLegend legendItems={legendItems} variant="inline" />

        <div className="flex-1 min-h-0">
          {isContainerReady && <RoastLineChart variant="inline" {...chartProps} />}
        </div>
      </div>

      <Modal isOpen={isChartModalOpen} onClose={() => setIsChartModalOpen(false)} title="Roast Profile Chart" maxWidth="7xl" className="px-2 sm:px-3">
        <div className="h-[45vh] sm:h-[70vh] flex flex-col">
          <RoastChartLegend legendItems={legendItems} variant="modal" />

          <div className="flex-1 min-h-0">
            <RoastLineChart variant="modal" {...chartProps} />
          </div>
        </div>
      </Modal>
    </>
  );
});
